import { cn } from '@/lib/utils';
import { BRAND } from './brand.config';

/*
 * Mockups de tela do DOMÍNIO (agenda do dia, recebimento no caixa, fidelidade e painel do dia), todos rotulados
 * "exemplo ilustrativo". Nomes e valores são fictícios. Os campos são os que o produto realmente tem.
 */

function Head({ title }: { title: string }) {
  return (
    <div className="lp-mock-head flex items-baseline justify-between gap-3 px-4 py-3">
      <p className="whitespace-nowrap font-display text-base font-semibold">{title}</p>
      <p className="lp-muted whitespace-nowrap text-xs">exemplo ilustrativo</p>
    </div>
  );
}

/* ---------- Agenda do dia ---------- */

type Status = 'completed' | 'in_progress' | 'confirmed' | 'requested';

const STATUS_LABEL: Record<Status, { label: string; chip: string }> = {
  completed: { label: 'concluído', chip: 'lp-chip-ok' },
  in_progress: { label: 'em atendimento', chip: 'lp-chip-warn' },
  confirmed: { label: 'confirmado', chip: 'lp-chip-idle' },
  requested: { label: 'solicitado', chip: 'lp-chip-idle' },
};

const SLOTS: readonly { time: string; what: string; short: string; who: string; status: Status; hl?: boolean }[] = [
  { time: '09:00', what: 'Corte e barba, Rafael', short: 'Corte, Rafael', who: 'Diego', status: 'completed' },
  { time: '10:00', what: 'Barba, Marcos', short: 'Barba, Marcos', who: 'Diego', status: 'in_progress' },
  { time: '10:30', what: 'Corte degradê, Lucas', short: 'Corte, Lucas', who: 'Bruno', status: 'requested', hl: true },
  { time: '11:30', what: 'Corte social, André', short: 'Corte, André', who: 'Bruno', status: 'confirmed' },
];

/** `compact` encurta rótulos e some com a 4ª linha, para sobrepor pouco da foto. */
export function AgendaPreview({ className, compact }: { className?: string; compact?: boolean }) {
  const rows = compact ? SLOTS.slice(0, 3) : SLOTS;
  return (
    <figure className={cn('lp-mock', className)}>
      <Head title={compact ? 'Agenda de hoje' : 'Agenda de sexta-feira'} />
      <ol>
        {rows.map((s) => (
          <li key={s.time} className={cn('lp-mock-row flex items-center gap-3 px-4 py-3', s.hl && 'lp-mock-hl')}>
            <span className="tabular w-11 shrink-0 text-sm font-semibold">{s.time}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{compact ? s.short : s.what}</span>
              <span className="lp-muted block truncate text-xs">{s.who}, barbeiro</span>
            </span>
            <span className={cn('lp-chip whitespace-nowrap', STATUS_LABEL[s.status].chip)}>{STATUS_LABEL[s.status].label}</span>
          </li>
        ))}
      </ol>
      <figcaption className="sr-only">Atendimentos do dia por profissional, com o status de cada um: solicitado, confirmado, em atendimento ou concluído.</figcaption>
    </figure>
  );
}

/* ---------- Recebimento no caixa ---------- */

const PAYS = [
  { what: 'Pix', value: 'R$ 40,00', chip: 'confirmado', tone: 'ok' },
  { what: 'Cartão de débito', value: 'R$ 30,00', chip: 'confirmado', tone: 'ok' },
  { what: 'Comissão de Diego', value: '40%', chip: 'regra geral', tone: 'idle' },
] as const;

export function CashierPreview({ className, compact }: { className?: string; compact?: boolean }) {
  return (
    <figure className={cn('lp-mock', className)}>
      <Head title={compact ? 'Recebimento' : 'Recebimento, caixa aberto'} />
      <div className="lp-mock-row px-4 py-3">
        <p className="truncate text-sm font-medium">Corte e barba, Rafael</p>
        <p className="lp-muted truncate text-xs">Pago em 2 meios</p>
      </div>
      <ul>
        {PAYS.map((p, i) => (
          <li key={p.what} className={cn('lp-mock-row flex items-center gap-3 px-4 py-3', i === 2 && 'lp-mock-hl')}>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{p.what}</span>
              <span className="tabular lp-muted block truncate text-xs">{p.value}</span>
            </span>
            <span className={cn('lp-chip whitespace-nowrap', `lp-chip-${p.tone}`)}>{p.chip}</span>
          </li>
        ))}
      </ul>
      <figcaption className="sr-only">Recebimento dividido em dois meios de pagamento, com a comissão do profissional calculada.</figcaption>
    </figure>
  );
}

/* ---------- Fidelidade ---------- */

const LOYALTY = [
  { name: 'Programa de pontos', short: 'Pontos', hint: 'saldo atual', chip: '120 pts', tone: 'ok' },
  { name: 'Último atendimento', short: 'Último corte', hint: 'pagamento confirmado', chip: '+15 pts', tone: 'ok' },
  { name: 'Pacote de 4 cortes', short: 'Pacote', hint: 'uso no atendimento', chip: '3 restantes', tone: 'warn' },
] as const;

export function LoyaltyPreview({ className, compact }: { className?: string; compact?: boolean }) {
  return (
    <figure className={cn('lp-mock', className)}>
      <Head title={compact ? 'Fidelidade' : 'Fidelidade do cliente'} />
      <div className="lp-mock-row px-4 py-3">
        <p className="truncate text-sm font-medium">Rafael</p>
        <p className="lp-muted truncate text-xs">Aceita campanhas por e-mail</p>
      </div>
      <ul>
        {LOYALTY.map((l, i) => (
          <li key={l.name} className={cn('lp-mock-row flex items-center gap-3 px-4 py-3', i === 1 && 'lp-mock-hl')}>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{compact ? l.short : l.name}</span>
              <span className="lp-muted block truncate text-xs">{l.hint}</span>
            </span>
            <span className={cn('lp-chip whitespace-nowrap', `lp-chip-${l.tone}`)}>{l.chip}</span>
          </li>
        ))}
      </ul>
      <figcaption className="sr-only">Fidelidade de um cliente: saldo de pontos, pontos do último atendimento e saldo do pacote.</figcaption>
    </figure>
  );
}

/* ---------- Painel do dia (faixa de 4 números) ---------- */

/** Mockup, NUNCA resultado de clientes: o rótulo "exemplo ilustrativo" fica visível no próprio quadro. */
export function PanelPreview() {
  const p = BRAND.panel;
  return (
    <figure className="lp-panel">
      <div className="lp-panel-head flex items-center justify-between gap-3 px-5 py-3 sm:px-7">
        <p className="font-display text-base font-semibold">Painel do dia</p>
        <p className="lp-panel-tag whitespace-nowrap">{p.label}</p>
      </div>
      <ul className="grid grid-cols-2 lg:grid-cols-4">
        {p.items.map((it) => (
          <li key={it.label} className="lp-panel-item px-5 py-5 sm:px-7 sm:py-6">
            <p className="tabular lp-accent-text font-display text-3xl font-bold leading-none sm:text-4xl">{it.value}</p>
            <p className="mt-2 text-[0.95rem] font-medium">{it.label}</p>
            <p className="lp-muted mt-0.5 text-sm">{it.hint}</p>
          </li>
        ))}
      </ul>
      <figcaption className="sr-only">Quatro indicadores de exemplo do painel do dia: atendimentos, faturamento, ocupação e ticket médio. Valores fictícios.</figcaption>
    </figure>
  );
}
